import { NoSsr } from "@mui/base";
import type { FunctionComponent } from "react";
import { Suspense, useEffect } from "react";
import { graphql, useQueryLoader } from "react-relay";

import { ArticleSpeechSkipContainer } from "~/components/article/article-speech";
import { DigitalArchiveLinkImage } from "~/components/schema-render/common/link/digital-archive-link/digital-archive-link-image";
import type { ContentSchemaRenderProps } from "~/components/schema-render/content";
import { ImageSizeStyle, useContentUtils } from "~/lib/hooks";
import { isValidUrl } from "~/lib/utils";
import type { genericPostiesArticleDigitalArchiveLinkCheckPathQuery } from "~/queries/__generated__/genericPostiesArticleDigitalArchiveLinkCheckPathQuery.graphql";

import {
  ImageContainer,
  LeftContainer,
  RightContainer,
  RootContainer,
  StyledButton,
  TitleContainer,
  TitleText,
} from "./styles";

export const query = graphql`
  query genericPostiesArticleDigitalArchiveLinkCheckPathQuery($url: String!, $imageSize: String!) {
    checkPath(url: $url) {
      ...digitalArchiveLinkImageUrlPath
      ...digitalArchiveLinkUpdatedDateUrlPath
    }
  }
`;

export type Props = {
  href?: string;
  lazyload?: boolean;
} & ContentSchemaRenderProps;

export const GenericPostiesArticleDigitalArchiveLink: FunctionComponent<Props> = ({
  href = "",
  lazyload,
  schemaNode,
}) => {
  const [queryReference, loadQuery, disposeQuery] =
    useQueryLoader<genericPostiesArticleDigitalArchiveLinkCheckPathQuery>(query);

  const { extractTextFromSchemaNodes } = useContentUtils();

  useEffect(() => {
    if (!isValidUrl(href)) return;

    const url = new URL(href);
    loadQuery({ imageSize: ImageSizeStyle["237x147"], url: url.pathname });

    return disposeQuery;
  }, [loadQuery, disposeQuery, href]);

  return (
    <ArticleSpeechSkipContainer>
      <RootContainer>
        <LeftContainer>
          <TitleContainer>
            <TitleText>{extractTextFromSchemaNodes(schemaNode)}</TitleText>
          </TitleContainer>
          <StyledButton variant="button">READ MORE</StyledButton>
        </LeftContainer>

        <RightContainer>
          <ImageContainer>
            <NoSsr>
              <Suspense>
                {queryReference && (
                  <DigitalArchiveLinkImage lazyload={lazyload} queryReference={queryReference} />
                )}
              </Suspense>
            </NoSsr>
          </ImageContainer>
        </RightContainer>
      </RootContainer>
    </ArticleSpeechSkipContainer>
  );
};

GenericPostiesArticleDigitalArchiveLink.displayName = "GenericPostiesArticleDigitalArchiveLink";
